import { makeChoices } from "../../distractors";
import { numUnit, simplify, text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, numeric, q } from "../helpers";

const KMH = { pl: "km/h", en: "km/h" };
const KM = { pl: "km", en: "km" };
const H = { pl: "h", en: "h" };
const MIN = { pl: "min", en: "min" };

const VEHICLES = [
  { pl: "Wóz kupca Milo jedzie", en: "Merchant Milo's cart travels" },
  { pl: "Łódź Finna płynie", en: "Finn's boat sails" },
  { pl: "Bohater biegnie", en: "The hero runs" },
  { pl: "Pociąg do Kopalni Koralików jedzie", en: "The train to the Bead Mines travels" },
];

function tier1(rng: Rng): QuestionDraft {
  const v = rng.pick([5, 10, 12, 15, 20, 30, 40, 50, 60, 70, 80, 90]);
  const t = rng.int(1, 5);
  const s = v * t;
  const who = rng.pick(VEHICLES);
  const choice4 = makeChoices(
    rng,
    s,
    [v + t, v / t, s + v, s - v],
    (r) => r.int(10, 400),
    (n) => numUnit(n, "km"),
  );
  return q(
    both(
      `${who.pl} z prędkością ${v} km/h przez ${t} h. Jaką drogę pokona?`,
      `${who.en} at ${v} km/h for ${t} h. What distance is covered?`,
    ),
    numeric(s, KM),
    [`Droga = prędkość · czas.`, `${v} km/h · ${t} h = ${s} km.`],
    [`Distance = speed × time.`, `${v} km/h × ${t} h = ${s} km.`],
    { choice4 },
  );
}

function tier2(rng: Rng): QuestionDraft {
  const v = rng.pick([5, 10, 12, 15, 20, 30, 40, 50, 60, 80, 100]);
  const t = rng.int(2, 6);
  const s = v * t;
  const who = rng.pick(VEHICLES);
  if (rng.chance(0.5)) {
    const choice4 = makeChoices(
      rng,
      v,
      [Math.round(v / 2), s - t, s + t, v + 10],
      (r) => r.int(5, 120),
      (n) => numUnit(n, "km/h"),
    );
    return q(
      both(
        `${who.pl} ${s} km w ${t} h. Jaka to prędkość?`,
        `${who.en} ${s} km in ${t} h. What is the speed?`,
      ),
      numeric(v, KMH),
      [`Prędkość = droga : czas.`, `${s} km : ${t} h = ${v} km/h.`],
      [`Speed = distance ÷ time.`, `${s} km ÷ ${t} h = ${v} km/h.`],
      { choice4 },
    );
  }
  const choice4 = makeChoices(
    rng,
    t,
    [s - v, t * 2, t + 1, t - 1],
    (r) => r.int(1, 12),
    (n) => numUnit(n, "h"),
  );
  return q(
    both(
      `${who.pl} ${s} km z prędkością ${v} km/h. Ile trwa podróż?`,
      `${who.en} ${s} km at ${v} km/h. How long does the trip take?`,
    ),
    numeric(t, H),
    [`Czas = droga : prędkość.`, `${s} km : ${v} km/h = ${t} h.`],
    [`Time = distance ÷ speed.`, `${s} km ÷ ${v} km/h = ${t} h.`],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  const v = rng.pick([20, 30, 40, 60, 90, 120]);
  const minutesOptions = [5, 10, 15, 20, 30, 45].filter((m) => Number.isInteger((v * m) / 60));
  const minutes = rng.pick(minutesOptions);
  const s = (v * minutes) / 60;
  const who = rng.pick(VEHICLES);
  const choice4 = makeChoices(
    rng,
    minutes,
    [minutes + 10, minutes * 2, Math.round(v / s), s * 6],
    (r) => r.int(5, 90),
    (n) => numUnit(n, "min"),
  );
  const { num, den } = simplify(s, v);
  const hoursText = den === 1 ? `${num}` : `${num}/${den}`;
  return q(
    both(
      `${who.pl} ${s} km z prędkością ${v} km/h. Ile minut trwa podróż?`,
      `${who.en} ${s} km at ${v} km/h. How many minutes does the trip take?`,
    ),
    numeric(minutes, MIN),
    [
      `Czas w godzinach: ${s} : ${v} = ${hoursText} h.`,
      `Zamieniamy na minuty: ${hoursText} · 60 = ${minutes} min.`,
    ],
    [
      `Time in hours: ${s} ÷ ${v} = ${hoursText} h.`,
      `Convert to minutes: ${hoursText} × 60 = ${minutes} min.`,
    ],
    { choice4 },
  );
}

export const speedDistanceTime: TopicDef = {
  id: "sp6.speed_distance_time",
  level: "sp6",
  name: text("Prędkość, droga, czas", "Speed, distance, time"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
